import { CopyOutlined } from "@ant-design/icons";
import { App, Flex } from "antd";
import { Form, InputNumber } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import {
    apiCall,
    getApiError,
    postMessengerReceivedDelivery,
} from "tm-shared/api";
import { PromoGood } from "tm-shared/types";
import { sleep } from "tm-shared/utils";

import { PointInfo } from "@/domains/point";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { WarningModal } from "@/shared/modals";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { getCheckoutsThunk } from "@/store/slices/ui";
import { ModalPayloads, ModalType } from "@/types/modals";
import { isManagerRole } from "@/utils/business/isManagerRole";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";
import { isOnline } from "@/utils/network/isOnline";

import { StyledCopyButton, StyledPromoTable } from "./Styled";
import { getTimesWord, getTotalGifts } from "./utils";

const FORM_ID = "promo-form";
const MAX_ALLOWED_LATENCY_MS = 2000;
const SUBMIT_DELAY_MS = 7_000;

export interface GiftItem {
    id: string;
    name: React.ReactNode;
    quantityAvailable: number;
}

interface PromoFormValues {
    gifts: Record<string, number>;
}

export interface PromoModalProps {
    payload: ModalPayloads[ModalType.Promos];
    onCloseModal: () => void;
}

const PromoModal: React.FC<PromoModalProps> = ({ onCloseModal, payload }) => {
    const { notification } = App.useApp();
    const dispatch = useAppDispatch();

    const { promoInfo, deliveryId } = payload;

    const role = useProtectedUserRoleSelector();

    const { userId, phone, route } = useAppSelector(
        (state) => ({
            userId: state.user.id,
            phone: state.user.phone,
            route: state.user.route,
        }),
        shallowEqual,
    );
    const activeSchedule = useAppSelector(
        (state) => state.manager.activeSchedule,
    );
    const offlineMode = useOfflineModeSelector();
    const point = usePointSelector(promoInfo?.pointId ?? "");

    const isManager = isManagerRole(role);

    const trueAgentId = isManager ? activeSchedule?.userId : userId;

    const [form] = Form.useForm<PromoFormValues>();
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (deliveryId) {
            postMessengerReceivedDelivery({
                deliveryId,
            });
        }
    }, [deliveryId]);

    const data = useMemo(() => {
        return Object.values(promoInfo?.promoGoods ?? []).map(
            (item: PromoGood) => ({
                id: item.uniquePresentCode,
                name: (
                    <Flex vertical gap={4}>
                        <PrimaryText>{item.name}</PrimaryText>

                        <SecondaryText>
                            Доступна кількість: {item.stockBalance}
                        </SecondaryText>
                    </Flex>
                ),
                quantityAvailable: item.stockBalance,
            }),
        );
    }, [promoInfo?.promoGoods]);

    const columns: ColumnsType<GiftItem> = useMemo(() => {
        return [
            {
                title: "Подарунок",
                dataIndex: "name",
                key: "name",
                width: "80%",
            },
            {
                title: "Обрана кількість",
                dataIndex: "selectedQuantity",
                key: "selectedQuantity",
                width: "20%",
                render: (_, record) => {
                    return (
                        <Form.Item
                            name={["gifts", record.id]}
                            initialValue={0}
                            style={{ margin: 0 }}
                            rules={[
                                {
                                    validator: (_, value: number) => {
                                        const { gifts } =
                                            form.getFieldsValue() || {};
                                        const totalWithoutCurrent =
                                            Object.entries(gifts).reduce(
                                                (acc, [key, val]) => {
                                                    if (key === record.id)
                                                        return acc;
                                                    return acc + (val || 0);
                                                },
                                                0,
                                            );
                                        const totalWithCurrent =
                                            totalWithoutCurrent + (value || 0);

                                        if (
                                            promoInfo?.promoScale !==
                                                undefined &&
                                            totalWithCurrent >
                                                promoInfo.promoScale
                                        ) {
                                            return Promise.reject(new Error());
                                        }

                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                min={0}
                                onFocus={(event) => event?.target?.select()}
                                max={record.quantityAvailable}
                                disabled={
                                    record.quantityAvailable === 0 || isLoading
                                }
                            />
                        </Form.Item>
                    );
                },
            },
        ];
    }, [form, isLoading, promoInfo.promoScale]);

    if (!promoInfo) return null;

    const handleWarningModalClose = () => {
        setIsWarningModalOpen(false);
    };

    const handleRejectPresent = async () => {
        setIsWarningModalOpen(true);
    };

    const handleWarningApproved = async () => {
        if (!promoInfo?.rejectCallback?.method) {
            onCloseModal();

            return;
        }

        setIsLoading(true);
        try {
            await apiCall({
                method: promoInfo?.rejectCallback?.method,
                params: {
                    ...promoInfo?.rejectCallback?.params,
                    deliveryId: deliveryId,
                },
            });

            setIsWarningModalOpen(false);
            form.resetFields();
        } catch (err) {
            console.error("API error:", err);
        } finally {
            setIsLoading(false);
            handleClosePromoModal();
        }
    };

    const handleClosePromoModal = () => {
        onCloseModal();
        form.resetFields();
    };

    const handleCopyPromoInfo = () => {
        copyObjectToClipboard(
            {
                userId: trueAgentId,
                contactId: phone,
                pointId: promoInfo?.pointId,
                promoGoods: promoInfo?.promoGoods,
                orderId: promoInfo?.orderId,
                promoId: promoInfo?.promoId,
                consumedId: promoInfo?.consumedId,
            },
            notification,
        );
    };

    const handleSubmit = async () => {
        if (offlineMode) {
            notification.error({
                title: "Відсутнє з'єднання з мережею, відновіть інтернет з'єднання та спробуйте ще раз",
            });
            return;
        }

        if (!promoInfo?.callback?.method) return;

        try {
            const values = await form.validateFields();

            const gifts = values?.gifts || {};

            const totalSum = getTotalGifts(gifts);

            if (totalSum === 0) {
                notification.error({
                    title: "Оберіть хоча б один подарунок",
                });
                return;
            }

            setIsLoading(true);

            const isGoodLatency = await isOnline(MAX_ALLOWED_LATENCY_MS);
            if (!isGoodLatency) {
                notification.error({
                    title: "Слабке інтернет-з'єднання, відновіть стабільне з'єднання та спробуйте ще раз",
                });
                setIsLoading(false);
                return;
            }

            const selectPresents = Object.entries(gifts).reduce(
                (acc, [id, count]) => {
                    if (count > 0) acc[id] = count;
                    return acc;
                },
                {} as Record<string, number>,
            );

            await sleep(SUBMIT_DELAY_MS);

            await apiCall({
                method: promoInfo.callback.method,
                params: {
                    ...promoInfo.callback.params,
                    selectPresents,
                    deliveryId: deliveryId,
                },
            });

            const routeId = isManager ? activeSchedule?.routeId : route?.uuid;

            if (routeId) {
                dispatch(
                    getCheckoutsThunk({
                        routeId,
                    }),
                );
            }

            notification.success({
                title: "Інвойс успішно створений",
            });

            handleClosePromoModal();
        } catch (error) {
            console.error("Validation failed:", error);

            const { code } = getApiError(error);

            if (String(code) === "-32015") {
                notification.error({
                    title: "Замовлення по цій акції вже створено",
                });
                handleClosePromoModal();
            } else {
                notification.error({
                    title: "Помилка при оформленні інвойсу",
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const checkMaxProducts = () => {
        const { gifts } = form.getFieldsValue();
        const totalSum = getTotalGifts(gifts);

        if (
            promoInfo?.promoScale !== undefined &&
            totalSum > promoInfo.promoScale
        ) {
            notification.error({
                title: `Максимальна кількість подарункових пляшок — ${promoInfo.promoScale} шт.`,
            });
        }
    };

    const handleValuesChange = () => {
        form.validateFields();
    };

    const pointAddress = point?.market?.building?.address?.name || "-";
    const pointFop = point?.market?.contragent?.name || "-";
    const pointName = point?.name || "-";

    return (
        <>
            <BaseModal
                open
                title=""
                closable={false}
                onClose={() => {}}
                actions={null}
            >
                <StyledCopyButton type="text" size="small">
                    <PrimaryText
                        fontSize={16}
                        fontWeight="500"
                        onClick={handleCopyPromoInfo}
                    >
                        {promoInfo?.title} <CopyOutlined />
                    </PrimaryText>
                </StyledCopyButton>

                <PointInfo fop={pointFop} address={pointAddress} />

                <SecondaryText>{promoInfo?.description}</SecondaryText>

                <PrimaryText>
                    Умови акції виконані {promoInfo?.promoScale}{" "}
                    {getTimesWord(promoInfo?.promoScale ?? 0)}. Оберіть
                    кількість подарункових пляшок:
                </PrimaryText>

                <Form
                    id={FORM_ID}
                    form={form}
                    onFinish={handleSubmit}
                    onValuesChange={handleValuesChange}
                >
                    <StyledPromoTable
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        locale={{ emptyText: "Немає товарів" }}
                        rowClassName={(record) =>
                            record.quantityAvailable === 0 ? "disabled-row" : ""
                        }
                    />
                </Form>

                <Flex vertical gap={8}>
                    <BaseButton
                        form={FORM_ID}
                        type="primary"
                        htmlType="submit"
                        bgColor="success"
                        loading={isLoading}
                        onClick={checkMaxProducts}
                    >
                        Оформити подарунок
                    </BaseButton>

                    <BaseButton
                        onClick={handleRejectPresent}
                        disabled={isLoading}
                        type="default"
                    >
                        Не оформляти
                    </BaseButton>
                </Flex>
            </BaseModal>

            <WarningModal
                isModalOpened={isWarningModalOpen}
                confirm={handleWarningApproved}
                cancel={handleWarningModalClose}
                maskClosable={false}
                isLoadingConfirm={isLoading}
            >
                <PrimaryText>
                    Ви впевнені, що хочете відмовитися від оформлення
                    подарункових пляшок для{" "}
                    <strong>
                        {pointName}, {pointAddress}
                    </strong>{" "}
                    ? Після підтвердження цієї дії повернутися до оформлення
                    буде неможливо!
                </PrimaryText>
            </WarningModal>
        </>
    );
};

export default PromoModal;
